import { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { PAYMENT_MAP, SAFE_METHODS, PLATFORM_FEE_RATE } from "@/lib/payments";
import { ShieldCheck, ArrowRight, CalendarClock, MapPin, BadgeCheck, MessageCircle, Lock } from "lucide-react";

// Full-page checkout: shows the booking + breakdown + safety notice, then a single Pay button.
export default function Checkout() {
  const { bookingId } = useParams();
  const [sp] = useSearchParams();
  const guestToken = sp.get("t") || "";
  const { user } = useAuth();
  const nav = useNavigate();
  const [booking, setBooking] = useState(null);
  const [hooman, setHooman] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        if (guestToken) {
          // Guest flow — hydrate via the token-scoped endpoint (no auth).
          const { data } = await api.get(`/bookings/guest/${guestToken}`);
          setBooking({
            id: data.id, service_title: data.service_title, price: data.price,
            date: data.date, notes: data.notes, payment_method: "card",
            payment_status: data.payment_status,
          });
          setHooman(data.hooman);
        } else {
          const list = await api.get("/bookings/mine?role=customer");
          const b = (list.data || []).find(x => x.id === bookingId);
          if (!b) { setErr("Booking not found"); return; }
          setBooking(b);
          const u = await api.get(`/users/${b.hooman_id}`);
          setHooman(u.data.user);
        }
      } catch (e) { setErr(formatErr(e)); }
    })();
  }, [bookingId, guestToken]);

  if (err) return <div className="p-12 text-center text-[#B84B4B]" data-testid="checkout-error">{err}</div>;
  if (!booking || !hooman) return <div className="p-12 text-center text-[#8A8A8A]" data-testid="checkout-loading">Loading…</div>;

  const method = booking.payment_method || "card";
  const pm = PAYMENT_MAP[method];
  const safe = SAFE_METHODS.has(method);
  const subtotal = Number(booking.price || 0);
  const fee = Number(booking.platform_fee ?? Math.round(subtotal * PLATFORM_FEE_RATE * 100) / 100);
  const total = Number(booking.total ?? (subtotal + fee));

  const pay = async () => {
    setBusy(true); setErr("");
    try {
      if (safe) {
        const body = { booking_id: booking.id, origin_url: window.location.origin };
        if (guestToken) body.guest_token = guestToken;
        const { data } = await api.post("/payments/checkout", body);
        window.location.href = data.url;
      } else {
        // Bank/Crypto are settled directly. Just confirm and return to bookings.
        nav("/bookings");
      }
    } catch (e) { setErr(formatErr(e)); setBusy(false); }
  };

  return (
    <div className="max-w-5xl mx-auto px-6 sm:px-8 py-10" data-testid="checkout-page">
      <p className="overline mb-3">Checkout</p>
      <h1 className="h-display text-4xl mb-8">Almost there — one tap from a real human moment.</h1>

      <div className="grid lg:grid-cols-[1fr_360px] gap-8 items-start">
        {/* LEFT: booking + Hooman summary */}
        <div className="space-y-5">
          <div className="card p-6 flex items-center gap-5" data-testid="checkout-hooman">
            {hooman.avatar && <img src={hooman.avatar} alt="" className="h-16 w-16 rounded-full object-cover"/>}
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <h2 className="font-heading text-2xl">{hooman.name}</h2>
                {hooman.is_verified && <span className="badge badge-verified"><BadgeCheck size={12}/> Verified</span>}
              </div>
              <p className="text-sm text-[#4A4A4A]">{(hooman.categories?.length ? hooman.categories : [hooman.category]).filter(Boolean).join(" · ")}</p>
              <p className="text-xs text-[#8A8A8A] inline-flex items-center gap-1 mt-1"><MapPin size={12}/> {hooman.location}</p>
            </div>
            <Link to={`/messages/${hooman.id}`} className="btn-secondary !py-2 !px-4 text-sm" data-testid="checkout-message-hooman">
              <MessageCircle size={14}/> Message
            </Link>
          </div>

          <div className="card p-6" data-testid="checkout-booking-details">
            <p className="overline mb-3">Your booking</p>
            <p className="text-lg font-heading">{booking.service_title}</p>
            <p className="text-sm text-[#4A4A4A] inline-flex items-center gap-1.5 mt-2"><CalendarClock size={14}/> {new Date(booking.date).toLocaleString()}</p>
            {booking.notes && (<p className="mt-3 text-sm text-[#4A4A4A]"><span className="overline">Notes</span><br/>{booking.notes}</p>)}
          </div>

          <div className="card p-6" data-testid="checkout-payment-method">
            <p className="overline mb-3">Payment method</p>
            <div className="flex items-center gap-3">
              {pm?.Icon && <pm.Icon size={18}/>}
              <span className="font-medium">{pm?.label || method}</span>
              {safe ? (
                <span className="badge badge-verified ml-auto"><ShieldCheck size={12}/> Protected by Hoomanz</span>
              ) : (
                <span className="text-xs text-[#0044CC] ml-auto">Settled directly with {hooman.name.split(" ")[0]}</span>
              )}
            </div>
          </div>
        </div>

        {/* RIGHT: price breakdown & pay */}
        <aside className="card p-6 sticky top-24" data-testid="checkout-summary">
          <p className="overline mb-4">Summary</p>
          <div className="space-y-2 text-sm">
            <Row label="Subtotal" value={`$${subtotal.toFixed(2)}`} testid="checkout-row-subtotal"/>
            <Row label="Hoomanz Tip (2%)" value={`+$${fee.toFixed(2)}`} muted testid="checkout-row-fee"/>
          </div>
          <div className="mt-4 pt-4 border-t border-[#E5E5E3] flex items-center justify-between font-heading text-lg">
            <span>Total today</span><span data-testid="checkout-total">${total.toFixed(2)}</span>
          </div>
          {safe && (
            <p className="text-xs text-[#4A4A4A] mt-3 inline-flex items-center gap-1.5">
              <Lock size={12}/> Hoomanz holds your payment until you both confirm the session.
            </p>
          )}
          {err && <p className="text-sm text-[#B84B4B] mt-3" data-testid="checkout-pay-error">{err}</p>}
          <button onClick={pay} disabled={busy} className="btn-primary w-full mt-5" data-testid="checkout-pay-btn">
            {busy ? "Redirecting…" : (safe ? <>Pay ${total.toFixed(2)} <ArrowRight size={16}/></> : "Confirm — pay in person")}
          </button>
          <p className="text-[11px] text-[#8A8A8A] mt-3 text-center">By paying you agree to Hoomanz&apos; fair-use &amp; refund policy.</p>
        </aside>
      </div>
    </div>
  );
}

function Row({ label, value, muted, testid }) {
  return (
    <div className={`flex justify-between ${muted ? "text-[#4A4A4A]" : ""}`} data-testid={testid}>
      <span>{label}</span><span>{value}</span>
    </div>
  );
}
